// Slice 7: export scope picker + delivery. Lets the user export the whole audit or a subset of
// accounts (grouped by platform so "all Google", "just one CID", etc. all fall out), then download
// the Markdown or copy it to the clipboard. The Markdown itself is built CID-first by
// buildAuditMarkdown so the export reads as a per-account narrative.

import { useCallback, useMemo, useState } from 'react';
import { Button } from '@Components/Button';
import { CopyButton } from '@Components/CopyButton';
import { Design, Size } from '@Components/designConstants';

import { Checkbox } from '@Components/FormCheck';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { buildAuditMarkdown } from '../export/buildAuditMarkdown';
import { PlatformIcon } from './PlatformIcon';
import {
  ALL_PLATFORMS,
  PLATFORM_LABEL,
  type AuditRun,
  type Finding,
  type Platform
} from '../types';

export const ExportDialog = ({
  run,
  findings,
  onClose
}: {
  run: AuditRun;
  findings: Finding[];
  onClose: () => void;
}) => {
  // Only accounts that actually ran something are exportable.
  const accounts = useMemo(() => run.cids.filter(c => c.activeTemplates.length > 0), [run.cids]);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(accounts.map(a => a.cid)));

  const accountsByPlatform = useMemo(
    () =>
      ALL_PLATFORMS.map(p => ({
        platform: p,
        accounts: accounts.filter(a => a.platform === p)
      })).filter(g => g.accounts.length > 0),
    [accounts]
  );

  const findingCountForSelection = useMemo(
    () => findings.filter(f => selected.has(f.cid)).length,
    [findings, selected]
  );

  const { markdown, filename } = useMemo(
    () => buildAuditMarkdown(run, findings, selected),
    [run, findings, selected]
  );

  const allSelected = selected.size === accounts.length;

  function toggle(cid: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(cid)) {
        next.delete(cid);
      } else {
        next.add(cid);
      }
      return next;
    });
  }
  function setAll(on: boolean) {
    setSelected(on ? new Set(accounts.map(a => a.cid)) : new Set());
  }
  function setPlatform(platform: Platform, on: boolean) {
    setSelected(prev => {
      const next = new Set(prev);
      for (const a of accounts.filter(x => x.platform === platform)) {
        if (on) {
          next.add(a.cid);
        } else {
          next.delete(a.cid);
        }
      }
      return next;
    });
  }

  const download = useCallback(() => {
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [markdown, filename]);

  return (
    <div
      className='export-overlay'
      role='dialog'
      aria-modal='true'
      aria-label='Export audit'
      onClick={onClose}
    >
      <div className='export-dialog' onClick={e => e.stopPropagation()}>
        <header className='export-dialog__header'>
          <h2 className='export-dialog__title'>Export audit</h2>
          <button
            type='button'
            className='export-dialog__close'
            onClick={onClose}
            aria-label='Close'
          >
            ×
          </button>
        </header>

        <p className='export-dialog__hint'>
          Pick the accounts to include. The export is organized per account (account → template →
          section), so a single-account or single-platform export reads end-to-end.
        </p>

        <div className='export-dialog__selectall'>
          <Checkbox
            className='export-check'
            checked={allSelected}
            onChange={e => setAll(e.target.checked)}
            label='All accounts'
          />
        </div>

        <div className='export-dialog__groups'>
          {accountsByPlatform.map(g => {
            const groupCids = g.accounts.map(a => a.cid);
            const groupAll = groupCids.every(c => selected.has(c));
            return (
              <div key={g.platform} className='export-group'>
                <Checkbox
                  className='export-check export-check--group'
                  checked={groupAll}
                  onChange={e => setPlatform(g.platform, e.target.checked)}
                  label={
                    <span className={`platform-chip platform-chip--${g.platform}`}>
                      <PlatformIcon platform={g.platform} size={12} />
                      {PLATFORM_LABEL[g.platform]}
                    </span>
                  }
                />
                {g.accounts.map(a => (
                  <Checkbox
                    key={a.cid}
                    className='export-check export-check--account'
                    checked={selected.has(a.cid)}
                    onChange={() => toggle(a.cid)}
                    label={
                      <span>
                        {a.name} <span className='export-check__cid'>({a.cid})</span>
                      </span>
                    }
                  />
                ))}
              </div>
            );
          })}
        </div>

        <footer className='export-dialog__footer'>
          <span className='export-dialog__count'>
            {conditionallyPluralizeWithCount(selected.size, 'account')} ·{' '}
            {conditionallyPluralizeWithCount(findingCountForSelection, 'finding')}
          </span>
          <div className='export-dialog__actions'>
            <CopyButton label='Copy to clipboard' text={markdown} disabled={selected.size === 0} />
            <Button
              design={Design.BRAND}
              size={Size.SM}
              onClick={download}
              disabled={selected.size === 0}
            >
              Download .md
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
};
